import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { priceSubmissionService, PriceStats } from '../services/api';

interface Card {
  id: number;
  name: string;
  cardNumber: string;
  language?: string; // Add language
  type: string;
  rarity: string;
  setName: string;
  setCode: string;
  cost?: number;
  power?: number;
  gem?: number;
  gemColor?: string;
  symbol?: string;
  color?: string;
  imageUrl?: string;
  abilityText?: string;
}

const BrowseCardsPage = () => {
  const { t } = useTranslation();
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterRarity, setFilterRarity] = useState('');
  const [filterSet, setFilterSet] = useState('');
  const [filterCost, setFilterCost] = useState('');
  const [filterGem, setFilterGem] = useState('');
  // const [filterGemColor, setFilterGemColor] = useState(''); // Removed
  const [filterColor, setFilterColor] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [availableSets, setAvailableSets] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [cardPrices, setCardPrices] = useState<Record<number, PriceStats>>({});

  const [watchlist, setWatchlist] = useState<number[]>([]);

  const cardTypes = ['Avatar', 'Magic', 'Land', 'Construct', 'Life'];
  const rarities = [
    { label: 'Common', value: 'C' },
    { label: 'Rare', value: 'R' },
    { label: 'Super Rare', value: 'SR' },
    { label: 'Ultra Rare', value: 'UR' },
    { label: 'SCR', value: 'SCR' },
    { label: 'CBR', value: 'CBR' },
    { label: 'PR', value: 'PR' }
  ];
  const avatarColors = [
    { label: 'แดง', value: 'แดง' },
    { label: 'ฟ้า', value: 'ฟ้า' },
    { label: 'เขียว', value: 'เขียว' },
    { label: 'ม่วง', value: 'ม่วง' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchWatchlist();
    }
  }, []);

  const fetchWatchlist = async () => {
    try {
      const { watchlistService } = await import('../services/api');
      const response = await watchlistService.getWatchlist();
      if (response.data.success) {
        setWatchlist(response.data.data.map((item: any) => item.cardId));
      }
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
    }
  };

  const handleToggleWatchlist = async (cardId: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to use watchlist');
      return;
    }

    try {
      const { watchlistService } = await import('../services/api');
      if (watchlist.includes(cardId)) {
        await watchlistService.removeFromWatchlist(cardId.toString());
        setWatchlist(prev => prev.filter(id => id !== cardId));
      } else {
        await watchlistService.addToWatchlist(cardId.toString());
        setWatchlist(prev => [...prev, cardId]);
      }
    } catch (error) {
      console.error('Failed to update watchlist:', error);
    }
  };

  useEffect(() => {
    fetchCards();
  }, [searchTerm, filterType, filterRarity, filterSet, filterCost, filterGem, filterColor, page]);

  useEffect(() => {
    fetchAvailableSets();
  }, []);

  const fetchAvailableSets = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const response = await axios.get(`${API_URL}/cards/sets`);

      if (response.data.success) {
        setAvailableSets(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch available sets:', err);
    }
  };

  const fetchCardPrices = async (cardIds: number[]) => {
    if (cardIds.length === 0) return;

    try {
      // Use batch endpoint to fetch all prices in a single request
      const response = await priceSubmissionService.getBatchCardPriceStats(cardIds);
      setCardPrices(response.data.data);
    } catch (error) {
      console.error('Failed to fetch card prices:', error);
    }
  };

  const fetchCards = async () => {
    try {
      setLoading(true);
      setError(null);

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '24',
        sortBy: 'cardNumber',
        sortOrder: 'ASC'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filterType) params.append('type', filterType);
      if (filterRarity) params.append('rarity', filterRarity);
      if (filterSet) params.append('set', filterSet);
      if (filterCost) params.append('cost', filterCost);
      if (filterGem) params.append('gem', filterGem);
      // if (filterGemColor) params.append('gemColor', filterGemColor); // Removed
      if (filterColor) params.append('color', filterColor);

      const response = await axios.get(`${API_URL}/cards?${params.toString()}`);

      if (response.data.success) {
        const fetchedCards = response.data.data;
        setCards(fetchedCards);
        setTotalPages(response.data.pagination.totalPages);

        // Fetch prices for the loaded cards
        const cardIds = fetchedCards.map((card: Card) => card.id);
        fetchCardPrices(cardIds);
      } else {
        setError('Failed to load cards');
      }
    } catch (err: any) {
      console.error('Failed to fetch cards:', err);
      setError(err.message || 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCards();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('');
    setFilterRarity('');
    setFilterSet('');
    setFilterCost('');
    setFilterGem('');
    // setFilterGemColor(''); // Removed
    setFilterColor('');
    setPage(1);
  };

  const getGemIconColor = (gemColor?: string) => {
    if (!gemColor) return 'text-gray-400'; // default for clear/null
    switch (gemColor.toLowerCase()) {
      case 'red': return 'text-red-500';
      case 'blue': return 'text-blue-500';
      case 'green': return 'text-green-500';
      case 'purple': return 'text-purple-500';
      default: return 'text-gray-400';
    }
  };

  const getGemBackground = (gemColor?: string) => {
    if (!gemColor) return 'bg-gray-100 text-gray-700'; // default for clear/null
    switch (gemColor.toLowerCase()) {
      case 'red': return 'bg-red-100 text-red-700';
      case 'blue': return 'bg-blue-100 text-blue-700';
      case 'green': return 'bg-green-100 text-green-700';
      case 'purple': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeBackground = (type: string) => {
    switch (type) {
      case 'Avatar': return 'bg-red-500 text-white';
      case 'Magic': return 'bg-blue-500 text-white';
      case 'Construct': return 'bg-yellow-500 text-white';
      case 'Life': return 'bg-gray-500 text-white';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRarityColor = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'bg-gray-500 text-white';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'bg-blue-500 text-white';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'bg-purple-500 text-white';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'bg-yellow-500 text-white';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') {
      return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-white';
    }
    if (rarityLower === 'cbr') return 'bg-[#D2B48C] text-black';
    if (rarityLower === 'pr') return 'bg-[#F5F5F5] text-black border border-gray-300';
    return 'bg-gray-500 text-white';
  };

  const getTypeAbbreviation = (type: string) => {
    switch (type) {
      case 'Avatar': return 'A';
      case 'Magic': return 'M';
      case 'Land': return 'L';
      case 'Construct': return 'C';
      case 'Life': return 'Li';
      default: return type.charAt(0);
    }
  };

  const getRarityAbbreviation = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'C';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'R';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'SR';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'UR';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') return 'SCR';
    if (rarityLower === 'cbr') return 'CBR';
    if (rarityLower === 'pr') return 'PR';
    return rarity.toUpperCase();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">{t('browse.title')}</h1>
        <p className="text-gray-600">
          {t('browse.subtitle')}
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Bar, Card Set Filter, and Buttons */}
          <div className="flex flex-col md:flex-row md:items-end gap-2 md:gap-4">
            <div className="w-full md:flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('browse.searchLabel')}
              </label>
              <input
                type="text"
                placeholder={t('browse.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="w-full md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('browse.setCodeLabel')}
              </label>
              <select
                value={filterSet}
                onChange={(e) => setFilterSet(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('browse.allSets')}</option>
                {availableSets.map((set) => (
                  <option key={set} value={set}>{set}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <button
                type="submit"
                className="flex-1 md:flex-none px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t('browse.searchButton')}
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="flex-1 md:flex-none px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                {showFilters ? t('browse.hideFilters') : t('browse.showFilters')}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('browse.cardTypeLabel')}
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('browse.allTypes')}</option>
                  {cardTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Rarity Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('browse.rarityLabel')}
                </label>
                <select
                  value={filterRarity}
                  onChange={(e) => setFilterRarity(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('browse.allRarities')}</option>
                  {rarities.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Avatar Color Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('browse.avatarColorLabel')}
                </label>
                <select
                  value={filterColor}
                  onChange={(e) => setFilterColor(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('browse.allColors')}</option>
                  {avatarColors.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Cost Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('browse.costLabel')}
                </label>
                <input
                  type="number"
                  placeholder={t('browse.costPlaceholder')}
                  value={filterCost}
                  onChange={(e) => setFilterCost(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>

              {/* Gem Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('browse.gemLabel')}
                </label>
                <input
                  type="number"
                  placeholder={t('browse.gemPlaceholder')}
                  value={filterGem}
                  onChange={(e) => setFilterGem(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>

              {/* Gem Color Filter Removed */}
              {/* 
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('browse.gemColorLabel')}
                </label>
                <select
                  value={filterGemColor}
                  onChange={(e) => setFilterGemColor(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('browse.allColors')}</option>
                  {gemColors.map((color) => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </select>
              </div> 
              */}
            </div>
          )}

          {/* Reset Button */}
          {(searchTerm || filterType || filterRarity || filterSet || filterCost || filterGem || filterColor) && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {t('browse.resetFilters')}
            </button>
          )}
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Cards Count */}
          <div className="mb-4 text-gray-600">
            {t('browse.foundCards', { count: cards.length })}
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
            {cards.map((card) => (
              <Link
                key={card.id}
                to={`/cards/${card.id}`}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden group flex flex-col"
              >
                {/* Card Image */}
                <div className="aspect-[2.5/3.5] bg-gray-100 overflow-hidden flex-shrink-0">
                  {card.imageUrl ? (
                    <img
                      src={card.imageUrl}
                      alt={card.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <div className="text-center p-4">
                        <div className="text-4xl mb-2">🎴</div>
                        <div className="text-xs">{t('home.noImage')}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Info */}
                <div className="p-3 flex flex-col flex-1 min-h-0">
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-xs text-gray-500">{card.cardNumber}</div>
                    {card.language && (
                      <span className={`text-[10px] px-1 rounded font-bold ${card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                        {card.language}
                      </span>
                    )}
                  </div>

                  {/* Name with Type and Rarity badges */}
                  <div className="flex items-start gap-1 mb-2">
                    <div className={`text-xs px-1.5 py-0.5 rounded font-bold ${getTypeBackground(card.type)}`}>
                      {getTypeAbbreviation(card.type)}
                    </div>
                    <div className={`text-xs px-1.5 py-0.5 rounded font-bold ${getRarityColor(card.rarity)}`}>
                      {getRarityAbbreviation(card.rarity)}
                    </div>
                    <div className="font-bold text-sm line-clamp-2 flex-1">{card.name}</div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
                    {card.cost !== undefined && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                        $ {card.cost}
                      </span>
                    )}
                    {card.gem !== undefined && (
                      <span className={`px-2 py-1 rounded ${getGemBackground(card.gemColor)}`}>
                        <span className={getGemIconColor(card.gemColor)}>💎</span> {card.gem}
                      </span>
                    )}
                    {card.power !== undefined && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                        ✊ {card.power}
                      </span>
                    )}
                  </div>

                  {/* Ability Text */}
                  {card.abilityText && (
                    <div className="text-xs text-gray-600 mb-2 line-clamp-3 italic">
                      {card.abilityText}
                    </div>
                  )}

                  {/* Price Info */}
                  <div className="mt-auto pt-2 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex-1">
                      {cardPrices[card.id]?.hasData ? (
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-gray-500">{t('browse.avgPrice')}</span>
                          <span className="text-sm font-bold text-blue-600">
                            ฿{cardPrices[card.id].avgPrice?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          {t('browse.noPriceData')}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleToggleWatchlist(card.id);
                      }}
                      className={`ml-2 p-1.5 rounded-full transition-colors ${watchlist.includes(card.id)
                        ? 'text-red-500 hover:bg-red-50'
                        : 'text-gray-400 hover:text-red-500 hover:bg-gray-50'
                        }`}
                      title={watchlist.includes(card.id) ? "Remove from Watchlist" : "Add to Watchlist"}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={watchlist.includes(card.id) ? "currentColor" : "none"} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Empty State */}
          {cards.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-xl text-gray-600 mb-2">{t('browse.noCardsFound')}</p>
              <p className="text-gray-500">{t('browse.tryAdjusting')}</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('browse.previous')}
              </button>

              <div className="flex items-center gap-1">
                {/* Always show first page */}
                {page > 2 && (
                  <>
                    <button
                      onClick={() => setPage(1)}
                      className="hidden sm:block px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      1
                    </button>
                    <span className="hidden sm:block px-1">...</span>
                  </>
                )}

                {/* Show current, prev, next */}
                {Array.from({ length: 3 }, (_, i) => page - 1 + i)
                  .filter(p => p > 0 && p <= totalPages)
                  .map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${page === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum}
                    </button>
                  ))}

                {/* Always show last page */}
                {page < totalPages - 1 && (
                  <>
                    <span className="hidden sm:block px-1">...</span>
                    <button
                      onClick={() => setPage(totalPages)}
                      className="hidden sm:block px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('browse.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrowseCardsPage;
